// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { AttributeTypeModel } from "@insite/client-framework/Types/ApiModels";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { useIsSignedInAndhasToBeSignedIn } from "@store/Framework/Store/Context/ContextHooks";
import useGetMultipleSalesInformations from "@store/Framework/Store/Data/Products/ProductsHooks/useGetMultipleSalesInformations";
import useParentProductState from "@store/Framework/Store/Foundation/Data/Products/ProductsHooks/useParentProductState";
import StoreProductMultipleSales from "@store/Library/Components/Foundation/StoreProduct/StoreProductMultipleSales";
import StoreSupercededProductLabel from "@store/Library/Components/Foundation/StoreSupercededProductLabel";
import ProductAttributes, { ProductAttributesStyles } from "@store/Overrides/Components/ProductAttributes";
import { contentSpacingStyles } from "@store/Themes/common/mixins/spacing";
import getWidgetComponent from "@toolkit/Framework/Common/Utilities/getWidgetComponent";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React from "react";
import { css } from "styled-components";

const PageTitle = getWidgetComponent("ProductDetails/ProductDetailsPageTitle");
const PartNumber = getWidgetComponent("ProductDetails/ProductDetailsPartNumber");
const ManufacturingPartNumber = getWidgetComponent("ProductDetails/ProductDetailsManufacturingPartNumber");
const ProductDetailsPrice = getWidgetComponent("ProductDetails/ProductDetailsPrice");
const ProductDetailsAddToCartButton = getWidgetComponent("ProductDetails/ProductDetailsAddToCartButton");
const ProductDetailsQuantityOrdered = getWidgetComponent("ProductDetails/ProductDetailsQuantityOrdered");
const ProductDetailsVariantOptions = getWidgetComponent("ProductDetailsVariant/ProductDetailsVariantOptionImages");
const ProductDetailsSpecifications = getWidgetComponent("ProductDetails/ProductDetailsSpecifications");
const ProductDetailsAvailability = getWidgetComponent("ProductDetails/ProductDetailsAvailability");
const ProductDetailsAddToListLink = getWidgetComponent("ProductDetails/ProductDetailsAddToListLink");

type Props = HasProduct & {
    selectedAttributes: string[];
    fallbackOnNormalAttributeType: boolean;
};

export interface StoreProductDetailsInformationsStyle {
    componentWrapper?: InjectableCss;
    productName?: InjectableCss;
    productSku?: InjectableCss;
    productDetails?: InjectableCss;
    productDetailsAction?: InjectableCss;
    priceAndAvailabilityWrapper?: InjectableCss;
    productDetailsActionButton?: InjectableCss;
    productDetailsPriceAvailability?: InjectableCss;
    attributes?: ProductAttributesStyles;
    infoBoxMultipleSales?: InjectableCss;
}

const sectionsSpacing = pxToRem(25);

const productDetailsActionButtonMargin = pxToRem(10);

const styles = {
    componentWrapper: {
        css: css`
            max-width: 100%;
        `,
    },
    productDetails: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            ${contentSpacingStyles(0, 0, sectionsSpacing)}
            padding-bottom: ${sectionsSpacing};
            border-bottom: ${pxToRem(1)} solid ${getColor("common.border")};

            > * {
                ${contentSpacingStyles(0, 0, pxToRem(15))}
            }
        `,
    },
    productName: {
        css: css`
            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            max-width: ${pxToRem(425)};
                        `,
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    productSku: {
        css: css`
            display: flex;
            width: 100%;

            > * {
                &:nth-child(2) {
                    position: relative;
                    &::after {
                        background-color: ${getColor("tertiary")};
                    }
                }
            }

            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                            line-height: ${({ theme }) => theme.design.typography.lineHeight.larger};
                        `,
                        null,
                        css`
                            flex-direction: row;
                            line-height: ${({ theme }) => theme.design.typography.lineHeight.small};

                            > * {
                                display: flex;
                                ${contentSpacingStyles(0, "${pxToRem(25)", 0)}}

                                &:nth-child(2) {
                                    position: relative;

                                    &::after {
                                        content: "";
                                        position: absolute;
                                        top: 50%;
                                        left: ${pxToRem(-12)};
                                        width: ${pxToRem(1)};
                                        height: ${({ theme }) => theme.design.typography.size.smaller};
                                        transform: translateY(-50%);
                                    }
                                }
                            }
                        `,
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    productDetailsAction: {
        css: css`
            display: flex;
            width: 100%;

            > * {
                align-self: center;
                ${contentSpacingStyles(0, "1em", 0, 0)}

                &:first-child {
                    margin-right: 1em;
                }
            }
        `,
    },
    priceAndAvailabilityWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            width: 100%;

            > * {
                align-self: center;
                ${contentSpacingStyles(0, "1em", 0, 0)}

                &:first-child {
                    margin-right: 1em;
                }
            }
        `,
    },
    productDetailsActionButton: {
        css: css`
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            min-width: 80%;
            margin-bottom: -${productDetailsActionButtonMargin};

            > * {
                ${contentSpacingStyles(0, "1.2em", 0, 0)}
                margin-bottom: ${productDetailsActionButtonMargin};
            }
        `,
    },
    attributes: {
        attributeTypeText: {
            css: css`
                font-size: ${({ theme }) => theme.design.typography.size.base};
            `,
        },
        attributeValueText: {
            css: css`
                font-size: ${({ theme }) => theme.design.typography.size.base};
            `,
        },
    },
    productDetailsPriceAvailability: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    infoBoxMultipleSales: {
        css: css`
            max-width: 20em;
            margin: 0 0 0.6em;
        `,
    },
};

const StoreProductDetailsInformationImages: React.FC<Props> = ({
    product,
    product: { attributeTypes, detail, variantTraits, specifications, isVariantParent },
    selectedAttributes,
    fallbackOnNormalAttributeType,
}) => {
    const isSignedInAndhasToBeSignedIn = useIsSignedInAndhasToBeSignedIn();
    const { multipleSaleQty } = useGetMultipleSalesInformations();
    const selectedAttributesTypes: AttributeTypeModel[] = [];

    if (selectedAttributes && selectedAttributes?.length) {
        selectedAttributes?.forEach(x => {
            attributeTypes?.forEach(y => {
                if (y.name === x) {
                    selectedAttributesTypes.push(y);
                }
            });
        });
    }

    const replacementProductId = detail?.replacementProductId;
    const parentProductState = useParentProductState();
    const hasAttributes = attributeTypes !== null && attributeTypes?.length !== 0;
    const showSelectedAttributes = selectedAttributes?.length && selectedAttributesTypes.length;
    const showAttribute =
        showSelectedAttributes ||
        (fallbackOnNormalAttributeType && hasAttributes) ||
        (!selectedAttributes?.length && hasAttributes);
    const renderingSpecifications = specifications && specifications.length > 0;
    const showVariants = variantTraits?.some(x => x.traitValues?.length !== 0);
    const renderingParentSpecifications =
        parentProductState?.specifications && parentProductState?.specifications.length > 0;

    return (
        <StyledWrapper {...styles.componentWrapper}>
            <StyledWrapper {...styles.productDetails}>
                <StyledWrapper {...styles.productName}>
                    <PageTitle />
                </StyledWrapper>
                <StyledWrapper {...styles.productSku}>
                    <PartNumber />
                    <ManufacturingPartNumber />
                </StyledWrapper>
            </StyledWrapper>
            {replacementProductId && replacementProductId !== "" ? (
                <StyledWrapper {...styles.productDetails}>
                    <StoreSupercededProductLabel />
                </StyledWrapper>
            ) : null}
            {showAttribute || showVariants ? (
                <StyledWrapper {...styles.productDetails}>
                    <ProductAttributes
                        maximumNumberAttributeTypes={1}
                        selectedAttributesTypes={selectedAttributesTypes}
                        fallbackOnNormalAttributeType={fallbackOnNormalAttributeType}
                        extendedStyles={styles.attributes}
                    />
                    <ProductDetailsVariantOptions />
                </StyledWrapper>
            ) : null}
            {!isVariantParent && (
                <StyledWrapper {...styles.productDetails}>
                    {isSignedInAndhasToBeSignedIn && (
                        <>
                            <StyledWrapper {...styles.priceAndAvailabilityWrapper}>
                                <ProductDetailsPrice />
                                <ProductDetailsAvailability />
                            </StyledWrapper>
                            {multipleSaleQty > 1 && (
                                <StyledWrapper {...styles.infoBoxMultipleSales}>
                                    <StoreProductMultipleSales warning />
                                </StyledWrapper>
                            )}
                        </>
                    )}
                    <StyledWrapper {...styles.productDetailsAction}>
                        <ProductDetailsQuantityOrdered />
                        <StyledWrapper {...styles.productDetailsActionButton}>
                            <ProductDetailsAddToCartButton />
                            <ProductDetailsAddToListLink />
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            )}
            {renderingSpecifications || renderingParentSpecifications ? (
                <StyledWrapper {...styles.productDetails}>
                    <ProductDetailsSpecifications showDocuments={false} showAttributes={false} />
                </StyledWrapper>
            ) : null}
        </StyledWrapper>
    );
};

export default withProduct(StoreProductDetailsInformationImages);
